<script setup lang="ts">import { toRef as _toRef, computed as _computed } from 'vue';

import {computed} from 'vue'
import LogoComponent from '@toolify/client/src/modules/branding/LogoComponent.vue'
import {useNativeAppStore} from '@toolify/client/src/stores/AppTitlebarStore/useNativeAppStore'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import LogoIconSvgComponent from '@toolify/client/src/modules/branding/LogoIconSvgComponent.vue'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'

import WorkspaceTabBarComponent from '@toolify/client/src/modules/workspaceTabs/components/WorkspaceTabBarComponent.vue'
import PointerDragPreviewComponent from '@toolify/client/src/modules/ui/components/PointerDragPreviewComponent.vue'
import {useProvidePointerDragDrop} from '@toolify/client/src/modules/ui/composables/usePointerDragDrop'
import {useExperimentalFeatureFlag} from '@toolify/client/src/modules/featureFlags/composables/useExperimentalFeatureFlag'
import {ExperimentalFeatureFlagType} from '@toolify/server/src/models/mongoose/UserModel/enum/ExperimentalFeatureFlagType'
import {useRoute} from 'vue-router'

import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {isGuestAuthRouteName} from '@toolify/client/src/modules/authentication/functions/isGuestAuthRouteName'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'

// interface Emits {
//   (e: 'event1', payload: string): void
// }
//
// interface Props {
//
// }
//
// const props = withDefaults(defineProps<Props>(), {})
//
// const emit = defineEmits<Emits>()

const nativeAppStore = useNativeAppStore()
const authStore = useAuthStore()
const route = useRoute()
const __$temp_1 = (useThemeVariables()),
  getThemeVariable = _toRef(__$temp_1, 'getThemeVariable');
const {isExperimentalFeatureFlagEnabled} = useExperimentalFeatureFlag()
useProvidePointerDragDrop()

const useAuthSurfaceBackground = computed(() => {
  if(!getThemeVariable.value(ThemeVariableType.UseModernAuthLayout)) {
    return false
  }
  return isGuestAuthRouteName(route.name) || route.name === RouteName.CreateWorkspace
})

const hideQuickNavigation = computed(() => {
  return isExperimentalFeatureFlagEnabled.value(ExperimentalFeatureFlagType.EnableResourceHeaderSearch)
})

const currentUser = _computed(() => {
  if (authStore.currentEntity?.entityType === EntityType.User) {
    return authStore.currentEntity.payload as IUserResponseInstance
  }
  return null
})

const shouldShowDevTools = _computed(() => {
  return !import.meta.env.PROD || !!currentUser.value?.isSuperAdmin
})
</script>

<template>
  <div
    class="native-window-header-wrapper"
    :class="{
      'is-window-in-fullscreen': nativeAppStore.isWindowInFullscreen,
      'hide-quick-navigation': hideQuickNavigation,
      'is-auth-surface': useAuthSurfaceBackground,
    }"
  >
    <div class="native-window-header">
      <div class="logos">
        <!--      <div class="logo-icon-wrapper">-->
        <!--        <LogoComponent isShort :color="ColorVariantType.Neutral"/>-->
        <!--      </div>-->
        <LogoIconSvgComponent :size="16"/>
        <LogoComponent :color="ColorVariantType.Neutral"/>
      </div>
      <div class="header-tabs">
        <WorkspaceTabBarComponent/>
      </div>
      <div class="buttons">
        <ButtonComponent
          :iconNameType="IconNameType.SquareTerminal"
          :style="StyleVariantType.ClearFilled"
          :color="ColorVariantType.Neutral"
          :iconWeightType="IconWeightType.Solid"
          v-if="shouldShowDevTools"
          @click="nativeAppStore.openDevTools()"
        />
        <ButtonComponent
          :iconNameType="IconNameType.Minus"
          :style="StyleVariantType.ClearFilled"
          :color="ColorVariantType.Neutral"
          :iconWeightType="IconWeightType.Solid"
          @click="nativeAppStore.minimizeApp()"
        />
        <ButtonComponent
          :iconNameType="nativeAppStore.isWindowInFullscreen || nativeAppStore.isWindowMaximized ? IconNameType.Compress : IconNameType.Expand"
          :style="StyleVariantType.ClearFilled"
          :color="ColorVariantType.Neutral"
          @click="nativeAppStore.toggleMaximizeOrExitFullscreen()"
          :iconWeightType="IconWeightType.Solid"
        />
        <ButtonComponent
          :iconNameType="IconNameType.Xmark"
          :style="StyleVariantType.ClearFilled"
          :color="ColorVariantType.Error"
          @click="nativeAppStore.exitApp()"
        />
      </div>
    </div>
    <PointerDragPreviewComponent/>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;


.native-window-header-wrapper {
  position: relative;
  height: map-get(variables.$sizes, size-2xl);

  &.is-window-in-fullscreen {
    height: 0.5rem;
    transition: height 0.2s cubic-bezier(0, 0.5, 0, 1), opacity 0.2s cubic-bezier(0, 0.5, 0, 1);
  }
}

.native-window-header {
  height: map-get(variables.$sizes, size-2xl);
  background: map-get(variables.$colors, surface-dim);

  .native-window-header-wrapper.is-auth-surface & {
    background: map-get(variables.$colors, surface);
  }
  -webkit-app-region: drag;
  display: grid;
  grid-template-columns: calc(3.75rem + 16rem + .125rem) 1fr auto;

  .native-window-header-wrapper.hide-quick-navigation & {
    grid-template-columns: calc(0.5rem + 16rem + .125rem) 1fr auto;
  }
  align-items: start;
  position: relative;
  @include variables.zIndex(nativeWindowHeader);

  .native-window-header-wrapper.is-window-in-fullscreen & {
    -webkit-app-region: no-drag;
    transform: translateY(-2.25rem);
    transition: transform .1s cubic-bezier(0, 0.5, 0, 1), opacity .1s cubic-bezier(0, 0.5, 0, 1);
    opacity: 0;
  }

  .native-window-header-wrapper.is-window-in-fullscreen:hover & {
    transform: translateY(0);
    opacity: 1;
  }

  
}

.logos {
  margin-left: 8px;
  display: flex;
  gap: map-get(variables.$spacing, space-1);
  align-items: center;
  height: map-get(variables.$sizes, size-xl);

  :deep(.logo) {
    font-weight: 700;
  }
}

.header-tabs {
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: stretch;
  padding-top: map-get(variables.$spacing, space-2);

  :deep(.workspace-tab-bar) {
    -webkit-app-region: no-drag;
    max-width: 100%;
  }
}

.logo-icon-wrapper {
  background: map-get(variables.$colors, primary);
  width: 17px;
  height: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: map-get(variables.$radiuses, radius-sm);
}

.buttons {
  display: flex;
  height: 100%;
  -webkit-app-region: no-drag;

  :deep(.button) {
    border-radius: 0;

    &.theme-neutral {
      &:hover {
        background: map-get(variables.$colors, container);
      }
    }
  }
}
</style>
